<template>
    <div class="create-stek">
        <Dialog
            header="Create your own Stek"
            @keyup.esc="cancelCreatingStek"
            @hide="cancelCreatingStek"
            v-model:visible="displayCreateStekModal"
            class="create-stek-dialog"
            :closable="false"
            :modal="true"
            :draggable="false"
        >
            <div v-if="errors.length">
                <div v-for="error in errors" :key="error">
                    <Message severity="error" :closable="false">
                        {{ error }}
                    </Message>
                </div>
            </div>
            <form
                v-on:submit.prevent="createStek"
                class="create-stek-form"
                aria-label="Create new Stek form"
            >
                <div class="field-info">
                    <div class="field-info-title">
                        <i class="pi pi-info-circle" />
                        <h4>What is a Stek?</h4>
                    </div>
                    <p>
                        Imagine your business as a well-oiled machine, where
                        every part needs to work together seamlessly to achieve
                        success. A Stek is like the blueprint for this machine.
                        It starts with the business strategies and processes
                        that guide what you do and why you do it. Then it also
                        includes the technical components like the software and
                        hardware required achieve your goals.
                    </p>
                    <p>
                        It's your business's living model — the foundation for
                        everything your team and AI agents will build on top.
                        Let's get started!
                    </p>
                </div>
                <Fluid class="field">
                    <label for="stekName" class="required-field">Name</label>
                    <IconField>
                        <InputIcon class="pi pi-bars" />
                        <InputText
                            id="stekName"
                            v-model="stekName"
                            aria-required="true"
                            :aria-invalid="!!nameError"
                            :class="{ 'p-invalid': nameError }"
                            autofocus
                        />
                    </IconField>
                    <small v-if="nameError" id="stekName-help" class="p-error">
                        {{ nameError }}
                    </small>
                    <small> The name cannot be changed later. </small>
                </Fluid>
                <Fluid class="field">
                    <label for="stekFqdn">Website</label>
                    <IconField>
                        <InputIcon class="pi pi-link" />
                        <InputText
                            id="stekFqdn"
                            v-model="fqdnInput"
                            @update:modelValue="handleFqdnChange"
                            aria-required="false"
                            :aria-invalid="!!fqdnError"
                            :class="{ 'p-invalid': fqdnError }"
                            placeholder="e.g. stekz.com"
                        />
                    </IconField>
                    <small v-if="fqdnError" class="p-error">
                        {{ fqdnError }}
                    </small>
                    <small>
                        Enter your website or domain name (e.g. example.com or
                        https://example.com). This helps to provide better
                        suggestions.
                    </small>
                </Fluid>
            </form>
            <template #footer>
                <Button
                    label="Cancel"
                    @click="cancelCreatingStek"
                    class="p-button-secondary"
                    :disabled="isLoading"
                />
                <Button
                    label="Create"
                    @click="createStek"
                    :loading="isLoading"
                    :disabled="!isValid || isLoading || !!fqdnError"
                    class="p-button-primary"
                />
            </template>
        </Dialog>
    </div>
</template>

<script setup lang="ts">
import { until } from "@vueuse/core";

import { computed, ref } from "vue";

import { useOrganizationManager } from "@/composables/organization-manager";
import router from "@/router";
import { useSteksManager, useSteksStore } from "@/stores/steks-store";
import { validateAndCleanFqdn, validateName } from "@/utils";

const displayCreateStekModal = ref(true);
const stekName = ref("");
const fqdnInput = ref("");
const stekFqdn = ref("");
const fqdnError = ref<string | undefined>();
const stekLogo = ref<File | null>(null);
const errors = ref<string[]>([]);
const isLoading = ref(false);
const { organization } = useOrganizationManager();
const { create } = useSteksStore();

const handleFqdnChange = (value: string | undefined) => {
    const result = validateAndCleanFqdn(value || "");
    fqdnError.value = result.error;
    stekFqdn.value = result.value;
};

const nameError = computed(() => {
    if (!stekName.value) return "";
    const errors = validateName(stekName.value);
    return errors.length ? errors[0] : "";
});

const isValid = computed(() => {
    return stekName.value && !nameError.value;
});

const cancelCreatingStek = () => {
    displayCreateStekModal.value = false;
    router.back();
};

const createStek = async () => {
    if (!isValid.value) return;
    if (!organization.value?.name) return;
    if (fqdnError.value) return;

    try {
        isLoading.value = true;
        await create(
            stekName.value,
            stekFqdn.value || undefined,
            stekLogo.value || undefined
        );

        // Wait for the newly created stek to appear in the steks list
        const { steks } = useSteksManager();
        await until(steks).toMatch(
            (steksList) =>
                steksList?.some(
                    (s) =>
                        s.name === stekName.value ||
                        s.identity === stekName.value
                ) ?? false,
            { timeout: 5000 }
        );

        router.push({
            name: "stekPage",
            params: {
                orgName: organization.value.name,
                stekName: stekName.value,
            },
        });
    } catch (error) {
        if (error instanceof Error && error.message.includes("Timeout")) {
            errors.value = [
                "Stek created but navigation timed out. Please refresh.",
            ];
        } else {
            try {
                errors.value = [];
                // @ts-expect-error error may not have a detail property
                // eslint-disable-next-line no-unsafe-optional-chaining
                for (const detail of error?.detail) {
                    errors.value.push(detail.message);
                }
            } catch {
                errors.value = [(error as Error).message];
            }
        }
    } finally {
        isLoading.value = false;
    }
};
</script>

<style lang="scss">
.create-stek-dialog {
    width: 90vw;
    max-width: 1080px;

    @media screen and (min-width: 768px) {
        width: 50vw;
    }
}
</style>

<style lang="scss" scoped>
.create-stek-form {
    .field {
        margin-bottom: 1.5rem;

        label {
            display: block;
            margin-bottom: 0.5rem;
        }
    }

    .field-info {
        position: relative;
        background-color: var(--surface-background-mild);
        padding: 1rem;
        border-left: 4px solid var(--stekz-primary-color);
        margin-bottom: 1rem;

        .field-info-title {
            display: flex;
            flex-direction: row;
            gap: 0.5rem;
            align-items: baseline;
        }

        p:not(:last-child) {
            margin-bottom: 1rem;
        }
    }

    .required-field::after {
        content: "*";
        color: var(--p-red-500);
        margin-left: 0.25rem;
    }

    small {
        display: block;
        margin-top: 0.25rem;
        color: var(--text-color-secondary);
        font-style: italic;

        &.p-error {
            color: var(--p-red-500);
        }
    }
}
</style>
